/**
 * Эвристический классификатор маркировки молочной продукции (Честный Знак).
 *
 * Детерминированный, полностью on-device (словарь ключевых слов + правила +
 * обработка исключений). Без внешних API и ML — гарантирует мгновенную работу
 * offline на старых планшетах Android 7.
 *
 * ВАЖНО: это ПОДСКАЗКА для оператора, а не юридически точный справочник ТН ВЭД.
 * Итоговое решение о маркировке остаётся за кассиром/директором — фильтр лишь
 * заранее подсвечивает позиции, похожие на подлежащие обязательной маркировке.
 */

/** Результат классификации одной позиции. */
export interface DairyMarkingVerdict {
  /** Похоже, что товар подлежит обязательной маркировке «Молоко». */
  requiresMarking: boolean
  /** Нормализованное ключевое слово, по которому сработало правило (для диагностики). */
  matchedKeyword?: string
  /** Сработало исключение (товар выведен из-под маркировки). */
  excludedBy?: string
}

/**
 * Ключевые слова молочной категории.
 *
 * Хранятся в нижнем регистре, уже нормализованными (см. normalize).
 * Сопоставление идёт по вхождению слова как отдельного токена, чтобы «сырок»
 * не ловился внутри «сырокопчёный».
 */
const DAIRY_KEYWORDS: readonly string[] = [
  'молоко',
  'молочный',
  'молочная',
  'молочное',
  'сливки',
  'сливочный',
  'сметана',
  'творог',
  'творожный',
  'творожная',
  'творожок',
  'кефир',
  'ряженка',
  'простокваша',
  'йогурт',
  'сырная',
  'сырный',
  'масло', // сливочное масло — уточняется правилами ниже
  'мороженое',
  'сгущёнка',
  'сгущенка',
  'сгущённое',
  'сгущенное',
  'ацидофилин',
  'варенец',
  'айран',
  'кумыс',
  'бифидок',
  'снежок',
  'брынза'
]

/**
 * Короткие/многозначные корни, для которых префиксное сравнение даёт ложные
 * срабатывания (например, «сыр» внутри «сырокопчёная колбаса», «сырьё»).
 * Такие ключи сопоставляются ТОЛЬКО как точный токен или форма с коротким
 * падежным окончанием.
 */
const EXACT_DAIRY_KEYWORDS: readonly string[] = [
  'сыр',
  'сыры',
  'сыра',
  'сыром',
  'сыре',
  'сырок',
  'сырка',
  'сырки',
  'сырков'
]

/**
 * Исключения — слова, выводящие товар из-под молочной маркировки, даже если
 * ключевое слово совпало. Примеры:
 * - «масло подсолнечное/растительное» — не молочное;
 * - «мыло/крем молочный», «сыр тофу» (растительный), «молочко для тела» и т.п.;
 * - детское питание до определённого возраста и спецпитание маркируются отдельно,
 *   но здесь мы исключаем явные немолочные/непищевые кейсы.
 */
const EXCLUSION_KEYWORDS: readonly string[] = [
  // Универсальные корни растительных масел — ловят сокращения «подсол.»,
  // «оливкового», «растит.» (сравнение идёт по token.startsWith(kw)).
  'подсол',
  'подсолн',
  'подсолнечное',
  'подсолнечн',
  'оливк',
  'оливковое',
  'растит',
  'растительное',
  'кокосовое',
  'сливочно-растительный',
  'спред',
  'тофу',
  'соевый',
  'соевое',
  'кокосовый',
  'миндальный',
  'овсяный',
  'овсяное',
  'мыло',
  'крем',
  'молочко', // косметическое «молочко для тела»
  'шоколад', // «молочный шоколад» маркируется в другой товарной группе
  'конфеты',
  'печенье',
  'каша' // сухие каши — отдельная история
]

/** Нормализация текста: нижний регистр, ё→е, схлопнутые пробелы, обрезка. */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Разбить нормализованную строку на токены по не-буквенно-цифровым границам. */
function tokenize(normalized: string): string[] {
  // \p{L} недоступен без флага 'u' на старых движках — используем явный диапазон кириллицы/латиницы.
  return normalized.split(/[^0-9a-zа-я]+/i).filter(Boolean)
}

/**
 * Проверить, встречается ли ключ как префикс какого-либо токена.
 *
 * Префиксное сравнение ловит падежи и формы («молоко», «молока», «молоком»,
 * «творожная», «творожный») без тяжёлой морфологии, но благодаря токенизации
 * не срабатывает на подстроках внутри неродственных слов.
 */
function matchByToken(tokens: string[], keywords: readonly string[]): string | undefined {
  for (const token of tokens) {
    for (const kw of keywords) {
      if (token === kw || token.startsWith(kw)) {
        return kw
      }
    }
  }
  return undefined
}

/**
 * Классифицировать наименование товара на предмет молочной маркировки.
 *
 * Алгоритм:
 * 1. Нормализуем и токенизируем название.
 * 2. Если сработало исключение — товар НЕ маркируется (возвращаем причину).
 * 3. Иначе ищем молочное ключевое слово — если нашли, товар подлежит маркировке.
 *
 * Исключения имеют приоритет над ключевыми словами (например, «масло
 * подсолнечное» → не маркируется, несмотря на «масло»).
 */
export function classifyDairyMarking(name: string): DairyMarkingVerdict {
  const normalized = normalize(name)
  if (!normalized) {
    return { requiresMarking: false }
  }
  const tokens = tokenize(normalized)

  const excludedBy = matchByToken(tokens, EXCLUSION_KEYWORDS)
  if (excludedBy) {
    return { requiresMarking: false, excludedBy }
  }

  // Точные (многозначные) корни — только полное совпадение токена.
  const exactMatch = tokens.find((t) => EXACT_DAIRY_KEYWORDS.includes(t))
  if (exactMatch) {
    return { requiresMarking: true, matchedKeyword: exactMatch }
  }

  const matchedKeyword = matchByToken(tokens, DAIRY_KEYWORDS)
  if (matchedKeyword) {
    return { requiresMarking: true, matchedKeyword }
  }

  return { requiresMarking: false }
}

/** Короткий булев-хелпер, когда детали вердикта не нужны. */
export function requiresDairyMarking(name: string): boolean {
  return classifyDairyMarking(name).requiresMarking
}
